import { useEffect, useState } from 'react';
import * as api from '../api';

export default function DashboardPage({ user }) {
  const [data, setData] = useState(null);
  const [mine, setMine] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (user?.role === 'admin') {
          const { data: d } = await api.getDashboard();
          if (!cancelled) setData(d);
        } else {
          const { data: d } = await api.getMyDashboard();
          if (!cancelled) setMine(d);
        }
      } catch (e) {
        if (!cancelled) setErr(e.response?.data?.error || 'Could not load dashboard');
      }
    })();
    return () => { cancelled = true; };
  }, [user]);

  if (err) return <div className="alert">{err}</div>;

  // ─── Employee dashboard ──────────────────────────────
  if (user?.role !== 'admin') {
    const assigned = mine?.assigned ?? 0;
    const completed = mine?.completed ?? 0;
    const inProgress = mine?.in_progress ?? 0;
    const notStarted = Math.max(0, assigned - completed - inProgress);
    const pct = assigned ? Math.round((completed / assigned) * 100) : 0;

    return (
      <div>
        <div className="notification">Welcome back! Keep going — every video you finish counts toward your training progress.</div>

        <div className="stat-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          <div className="stat-card">
            <div className="stat-label">Assigned Trainings</div>
            <div className="stat-val">{assigned}</div>
            <div className="stat-sub">Published training videos</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Completed</div>
            <div className="stat-val" style={{ color: 'var(--success)' }}>{completed}</div>
            <div className="stat-sub"><span className="stat-dot" style={{ background: 'var(--success)' }}></span>Great work so far</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">In Progress</div>
            <div className="stat-val" style={{ color: 'var(--teal)' }}>{inProgress}</div>
            <div className="stat-sub">Pick up where you left off</div>
          </div>
        </div>

        <div className="card">
          <div className="card-title">Overall Training Progress</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div className="progress-bar" style={{ flex: 1, height: 10 }}>
              <div className="progress-fill" style={{ width: pct + '%' }}></div>
            </div>
            <div style={{ fontWeight: 700, fontSize: 18, color: 'var(--teal)', minWidth: 52, textAlign: 'right' }}>{pct}%</div>
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 10 }}>
            {completed} of {assigned} completed · {inProgress} in progress · {notStarted} not started
          </div>
        </div>
      </div>
    );
  }

  // ─── Admin dashboard ─────────────────────────────────
  if (!data) return <p>Loading...</p>;

  return (
    <div>
      <div className="stat-grid">
        <div className="stat-card">
          <div className="stat-label">Total Employees</div>
          <div className="stat-val">{data.employees?.total || 0}</div>
          <div className="stat-sub"><span className="stat-dot" style={{ background: 'var(--success)' }}></span>{data.employees?.active || 0} active</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Training</div>
          <div className="stat-val" style={{ color: 'var(--teal)' }}>{data.videos?.total || 0}</div>
          <div className="stat-sub">{data.videos?.published || 0} published</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Assignments</div>
          <div className="stat-val">{data.assignments?.total || 0}</div>
          <div className="stat-sub">{data.assignments?.submitted || 0} submitted</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Quiz Attempts</div>
          <div className="stat-val" style={{ color: 'var(--success)' }}>{data.quizzes?.passed_count || 0}</div>
          <div className="stat-sub">{data.quizzes?.total || 0} total attempts</div>
        </div>
      </div>

      <div className="card">
        <div className="card-title">Recent Activity</div>
        {Array.isArray(data.recentActivity) && data.recentActivity.length > 0 ? (
          <table>
            <thead><tr><th>Employee</th><th>Action</th><th>Content</th><th>Date</th><th>Status</th></tr></thead>
            <tbody>
              {data.recentActivity.map((row, i) => (
                <tr key={i}>
                  <td style={{ fontWeight: 500 }}>{row.employee}</td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{row.type?.replace(/_/g, ' ')}</td>
                  <td>{row.content}</td>
                  <td style={{ fontSize: 12, color: 'var(--muted)' }}>{row.occurred_at ? new Date(row.occurred_at).toLocaleDateString() : ''}</td>
                  <td>
                    <span className={`badge ${row.passed ? 'badge-green' : 'badge-amber'}`}>
                      {row.passed ? (row.type === 'quiz_attempt' ? 'Passed' : 'Done') : 'Retry'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div style={{ textAlign: 'center', padding: '20px 0', color: 'var(--muted)', fontSize: 13 }}>No recent activity.</div>
        )}
      </div>
    </div>
  );
}
